// The diff/review panel as a custom tab (the `panelKinds` seam). Self-contained:
// it takes only its opaque tab `state` ({slug,repo,changesetId,number}) + the
// host `ctx`, then fetches the changeset + server diff + trunk VFS itself
// (react-query is app-global) and drives the workbench `compare` store so the
// reused <DiffBoardPane> renders. Comments (sidebar + node-pin overlay) live in
// the panel; the view-mode strip and the terminal lifecycle actions (Accept /
// Archive / Update-from-trunk) are this tab's chrome and hoist into the TopBar's
// active-tab slot — all reusing the existing proposals presentation + API.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DiffBoardPane,
  useCompare,
  type DiffState,
  type JsonValue,
  type WorkbenchHostContext,
} from "@archlang/app-shell/workbench";
import {
  getChangesetByNumber,
  acceptProposal,
  archiveChangeset,
  updateFromTrunk,
} from "../../../proposals/proposalsApi";
import { useChangesetDiff } from "../../../proposals/useChangesetDiff";
import { CommentsPanel } from "../../../proposals/CommentsPanel";
import { CommentPinOverlay } from "../../../proposals/CommentPinOverlay";
import { DiffModeTabStrip } from "../../DiffModeTabStrip";
import { HeaderSlotPortal } from "../../../app/headerSlot";
import { seedVfs } from "../../seedVfs";
import { cacheChangeset, invalidateChangesetLists } from "../../../proposals/changesetCache";
import { repoHome } from "../../../lib/paths";
import { useMe } from "../../../api/hooks/useMe";
import { ApiError, API_BASE } from "../../../api/client";
import { Button } from "../../../ui/Button";
import { Spinner } from "../../../ui/Spinner";
import { useToast } from "../../../ui/Toast";
import { useTranslation } from "../../../i18n";
import {
  useHistoryStore,
  setReviewPinMode,
  toggleReviewPinMode,
  clearPendingNodeComment,
} from "./historyStore";
import { parseReviewState, type ReviewState } from "./reviewState";

const EMPTY_MAP: ReadonlyMap<string, string> = new Map();

export function ReviewTabRenderer({
  state,
}: {
  readonly state: JsonValue;
  readonly ctx: WorkbenchHostContext;
}) {
  const parsed = parseReviewState(state);
  if (!parsed) {
    return <div className="p-4 text-sm text-red-300">Invalid review tab.</div>;
  }
  return <ReviewTab {...parsed} />;
}

function ReviewTab({ slug, repo, changesetId, number, title }: ReviewState) {
  const { t } = useTranslation("editor");
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { reviewPinMode, pendingNodeComment } = useHistoryStore();
  // Archive is destructive (retires the changeset), so it's confirm-gated —
  // first click reveals an inline "Archive this change?" confirm, matching the
  // pre-plugin review chrome.
  const [confirmArchive, setConfirmArchive] = useState(false);

  const csQuery = useQuery({
    queryKey: ["changeset", slug, repo, number],
    queryFn: () => getChangesetByNumber(slug, repo, number),
    enabled: Number.isInteger(number) && number > 0,
  });
  const changeset = csQuery.data ?? null;
  // `useMe` kept for the Phase-B author-only "Update drafts" action.
  useMe();

  // Server-side model diff (trunk → feature) for the proposal.
  const gitDiffQuery = useChangesetDiff(slug, repo, changesetId, true, changeset?.state);
  const precomputedDiff = useMemo<DiffState | undefined>(() => {
    if (gitDiffQuery.isLoading) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return { changeSet: null, before: null, after: null, beforeEdges: [] as any, afterEdges: [] as any, beforeRaw: null, afterRaw: null, loading: true, error: null };
    }
    const pkg = gitDiffQuery.data?.packages[0];
    if (!pkg) return undefined;
    return {
      changeSet: pkg.changeSet,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      before: pkg.before.model.modules as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      after: pkg.after.model.modules as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      beforeEdges: pkg.before.edges as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      afterEdges: pkg.after.edges as any,
      beforeRaw: null,
      afterRaw: null,
      loading: false,
      error: gitDiffQuery.isError ? String(gitDiffQuery.error) : null,
    };
  }, [gitDiffQuery.data, gitDiffQuery.isLoading, gitDiffQuery.isError, gitDiffQuery.error]);

  // Trunk VFS for document (markdown) diff — the server payload carries model
  // data only. Feature-branch files come from the working store.
  const trunkRef = changeset?.trunkBranch ?? null;
  const trunkVfsQuery = useQuery({
    queryKey: ["vfs-seed-trunk", slug, repo, trunkRef],
    queryFn: async () =>
      trunkRef ? seedVfs({ slug, repo, ref: trunkRef, apiBase: API_BASE }) : new Map<string, string>(),
    enabled: !!trunkRef,
    staleTime: Infinity,
  });
  const trunkVfs: ReadonlyMap<string, string> = trunkVfsQuery.data ?? EMPTY_MAP;

  // Drive the workbench compare store so DiffBoardPane renders (studioMode uses
  // trunkVfs as "before"; `against` just needs to be non-null). Clear on unmount.
  const compare = useCompare();
  useEffect(() => {
    compare.setCompareAgainst({ vfs: trunkVfs as Map<string, string>, label: "trunk" });
    return () => compare.clearCompare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trunkVfs]);

  // Exit pin mode + drop any un-honored "Comment here" request when this review
  // tab unmounts (tab closed / switched away).
  useEffect(
    () => () => {
      setReviewPinMode(false);
      clearPendingNodeComment();
    },
    [],
  );

  function leaveToMain() {
    invalidateChangesetLists(queryClient, slug, repo);
    navigate(repoHome(slug, repo));
  }
  const acceptMutation = useMutation({
    mutationFn: () => acceptProposal(slug, repo, changesetId),
    onSuccess: (cs) => {
      toast.success(t("toast.accepted"));
      cacheChangeset(queryClient, slug, repo, cs);
      leaveToMain();
    },
    onError: (err: Error) =>
      toast.error(err instanceof ApiError && err.status === 409 ? t("error.trunkMovedAccept") : err.message),
  });
  const updateFromTrunkMutation = useMutation({
    mutationFn: () => updateFromTrunk(slug, repo, changesetId),
    onSuccess: (cs) => {
      toast.success(t("toast.mergedTrunk"));
      cacheChangeset(queryClient, slug, repo, cs);
      invalidateChangesetLists(queryClient, slug, repo);
      // The merge moved the branch TIP but neither query key changed (both key
      // on names/ids): without these, the editor keeps the pre-merge committed
      // tree (drafts then carry stale full-file content the next Update would
      // commit, silently reverting the merged-in trunk changes) and the review
      // tab keeps showing the pre-merge diff (staleTime: Infinity) right after
      // the "merged trunk" toast. Prefix-match vfs-seed: this component
      // doesn't know the route's baseRef.
      queryClient.invalidateQueries({ queryKey: ["vfs-seed", slug, repo] });
      queryClient.invalidateQueries({ queryKey: ["changeset-diff", slug, repo, changesetId] });
    },
    onError: (err: Error) =>
      toast.error(err instanceof ApiError && err.status === 409 ? t("error.trunkConflict") : err.message),
  });
  const archiveMutation = useMutation({
    mutationFn: () => archiveChangeset(slug, repo, changesetId),
    onSuccess: () => {
      toast.success(t("toast.archived"));
      leaveToMain();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (csQuery.isError) {
    return <div className="p-4 text-sm text-red-300">{String(csQuery.error)}</div>;
  }
  if (!changeset) {
    return (
      <div className="flex h-full items-center justify-center gap-3 text-slate-400">
        <Spinner />
      </div>
    );
  }

  const label = title?.trim() || changeset.title?.trim() || `#${number}`;
  const isProposed = changeset.state === "proposed";

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Review chrome hoisted into the top bar's active-tab slot: the diff is a
            tab like any other, so its controls sit in the one row of app chrome
            instead of a second toolbar stacked under the tab strip. It unmounts
            with the tab, which is what keeps the top bar honest about context. */}
        <HeaderSlotPortal slot="tab">
          {/* Diff view-mode tabs (split / overlay / processes / documents) —
              DiffBoard hides its own toolbar in studioMode, so surface them here. */}
          <DiffModeTabStrip />
          {isProposed ? (
            <>
              {confirmArchive ? (
                <span className="flex items-center gap-2 text-xs text-amber-200">
                  {t("chrome.archiveConfirm")}
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={archiveMutation.isPending}
                    onClick={() => archiveMutation.mutate()}
                  >
                    {archiveMutation.isPending ? t("chrome.archiving") : t("chrome.archive")}
                  </Button>
                  <button
                    type="button"
                    className="text-amber-300/80 hover:text-amber-100"
                    onClick={() => setConfirmArchive(false)}
                  >
                    {t("chrome.cancel")}
                  </button>
                </span>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => setConfirmArchive(true)}>
                  {t("chrome.archive")}
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={updateFromTrunkMutation.isPending}
                onClick={() => updateFromTrunkMutation.mutate()}
              >
                {updateFromTrunkMutation.isPending ? t("chrome.updatingFromTrunk") : t("chrome.updateFromTrunk")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={acceptMutation.isPending}
                onClick={() => acceptMutation.mutate()}
              >
                {acceptMutation.isPending ? t("chrome.accepting") : t("chrome.accept")}
              </Button>
            </>
          ) : null}
        </HeaderSlotPortal>
        <div className="relative min-h-0 min-w-0 flex-1">
          <DiffBoardPane
            currentLabel={label}
            studioMode
            precomputedDiff={precomputedDiff}
            trunkVfs={trunkVfs}
          />
          {isProposed ? (
            <CommentPinOverlay
              slug={slug}
              repo={repo}
              proposalId={changesetId}
              pinMode={reviewPinMode}
              onExitPinMode={() => setReviewPinMode(false)}
              pendingNodeId={pendingNodeComment}
              onConsumePending={clearPendingNodeComment}
            />
          ) : null}
        </div>
      </div>
      {isProposed ? (
        <CommentsPanel
          slug={slug}
          repo={repo}
          changesetId={changesetId}
          pinMode={reviewPinMode}
          onTogglePinMode={() => toggleReviewPinMode()}
        />
      ) : null}
    </div>
  );
}
